import { fileUrl } from './file';
import type {
  TaskBrandLogoOptions,
  TaskBrandLogoVariant,
  TaskUtils,
  TaskWorkspaceFields
} from './types';
import { warnSdkUtilFallback } from './warn';

// Widest-first, mirroring BrandKit::loginLogoFileId on the backend.
const DEFAULT_VARIANTS: TaskBrandLogoVariant[] = ['horizontal', 'wordmark', 'icon'];

function logoFileId(
  logos: unknown,
  variant: TaskBrandLogoVariant,
  mode: 'light' | 'dark'
): string | null {
  if (logos === null || typeof logos !== 'object') {
    return null;
  }

  const group = (logos as Record<string, unknown>)[variant];

  if (group === null || group === undefined || typeof group !== 'object') {
    return null;
  }

  const fileId = (group as Record<string, unknown>)[mode];

  return typeof fileId === 'string' && fileId !== '' ? fileId : null;
}

export function createBrandLogoUtil(workspaceFields: TaskWorkspaceFields): TaskUtils['brandLogo'] {
  return (options: TaskBrandLogoOptions | null = {}) => {
    if (options === null || typeof options !== 'object') {
      warnSdkUtilFallback('brandLogo', 'Options must be an object; returning null.');
      return null;
    }

    if (options.mode !== undefined && options.mode !== 'light' && options.mode !== 'dark') {
      warnSdkUtilFallback('brandLogo', 'The requested mode is unsupported; returning null.');
      return null;
    }

    const brandKit = workspaceFields['brand_kit'];
    const logos =
      brandKit !== null && typeof brandKit === 'object'
        ? (brandKit as { logos?: unknown }).logos
        : null;

    const requestedVariants = options.variants ?? DEFAULT_VARIANTS;

    if (!Array.isArray(requestedVariants)) {
      warnSdkUtilFallback('brandLogo', 'Variants must be an array; returning null.');
      return null;
    }

    const variants = requestedVariants.filter((variant): variant is TaskBrandLogoVariant =>
      DEFAULT_VARIANTS.includes(variant)
    );

    if (variants.length !== requestedVariants.length) {
      warnSdkUtilFallback('brandLogo', 'Unsupported variants were ignored.');
    }

    const modes: Array<'light' | 'dark'> =
      options.mode === 'dark' ? ['dark', 'light'] : ['light', 'dark'];

    for (const variant of variants) {
      for (const mode of modes) {
        const fileId = logoFileId(logos, variant, mode);

        if (fileId !== null) {
          return fileUrl(fileId);
        }
      }
    }

    return null;
  };
}
